import { Link, usePage } from "@inertiajs/react"

import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { InlineInput } from "@/components/ui/inline-input"
import {
  itemStockStatus,
  itemStockTone,
  type ItemStockRow,
} from "@/features/staff/item-stock/item-stock"
import { TILL_PATHS, tTill, type ShiftIdentity, type TillItemStock } from "@/features/staff/till"
import { formatDateTime } from "@/lib/formatters"
import { patchInline } from "@/lib/inline-update"
import { toneDotClass } from "@/lib/status-colors"

/**
 * Existencias while the drawer is open.
 *
 * The ceremony is over by now: the arqueo was signed at opening and what is
 * left is a column to glance at and, when the kitchen restocks, correct. So the
 * counts commit inline, with no submit and no dialog — the "light operational
 * field" case the inline-update rule describes.
 *
 * The correction goes to the till's own route rather than `/manage`, because
 * the manager screen is manager-only and the person who signed these numbers at
 * eight is the cashier the kitchen talks to at one. The link to the setup
 * screen is rendered only for a manager, for the reason the nav filters its own
 * links: a control whose only outcome is a redirect and an error is worse than
 * no control.
 *
 * The header never claims an arqueo that did not happen. A shift opened before
 * the restaurant switched dish counting on has no signed lines, and this says
 * so rather than dating the count to whenever the drawer opened.
 */

type TillStockPanelProps = {
  itemStock: TillItemStock
  shift: ShiftIdentity
  timeZone: string
}

function alerts(stocks: ItemStockRow[]) {
  const depleted = stocks.filter((stock) => stock.depleted).length
  const low = stocks.filter((stock) => stock.low).length

  return [
    depleted > 0 ? { tone: "disabled" as const, text: tTill("stock_alert_depleted", { count: depleted }) } : null,
    low > 0 ? { tone: "warning" as const, text: tTill("stock_alert_low", { count: low }) } : null,
  ].filter((entry) => entry !== null)
}

export function TillStockPanel({ itemStock, shift, timeZone }: TillStockPanelProps) {
  const { locationUser } = usePage<{ locationUser?: { role: string } | null }>().props
  const { stocks, stockCount } = itemStock
  const warnings = alerts(stocks)

  return (
    <Card density="compact" data-testid="shift-stock-panel">
      <CardHeader className="border-b">
        <CardTitle>{tTill("stock_panel_title")}</CardTitle>
        <CardDescription>
          {stockCount
            ? tTill("stock_panel_counted", {
                name: shift.openedBy ?? tTill("unknown_actor"),
                time: formatDateTime(shift.openedAt, { timeZone }),
              })
            : tTill("stock_panel_uncounted")}
          {stockCount && stockCount.edited > 0
            ? ` · ${tTill("stock_panel_edited", { count: stockCount.edited })}`
            : null}
        </CardDescription>
      </CardHeader>

      {stocks.length === 0 ? (
        <CardContent>
          <p className="text-sm text-muted-foreground" data-testid="shift-stock-panel-empty">
            {tTill("stock_panel_empty")}
          </p>
        </CardContent>
      ) : (
        <>
          {warnings.length > 0 ? (
            <CardContent className="flex flex-wrap items-center gap-3">
              {warnings.map((warning) => (
                <span
                  key={warning.tone}
                  className="flex items-center gap-1.5 text-xs text-muted-foreground"
                >
                  <span aria-hidden="true" className={toneDotClass(warning.tone)} />
                  {warning.text}
                </span>
              ))}
            </CardContent>
          ) : null}

          <ul className="divide-y border-t" data-testid="shift-stock-panel-list">
            {stocks.map((stock) => (
              <li
                key={stock.id}
                className="flex items-center gap-3 px-4 py-2"
                data-testid={`shift-stock-panel-row-${stock.id}`}
              >
                <span
                  aria-hidden="true"
                  className={toneDotClass(itemStockTone(itemStockStatus(stock)))}
                />
                <p className="min-w-0 flex-1 truncate text-sm font-medium">{stock.itemName}</p>
                <InlineInput
                  className="w-20 tabular-nums"
                  inputMode="numeric"
                  aria-label={tTill("stock_quantity_label", { name: stock.itemName })}
                  value={String(stock.quantity)}
                  data-testid={`shift-stock-panel-quantity-${stock.id}`}
                  // `preserveState` is not optional here. Without it the visit
                  // re-mounts the page, and the left column loses whatever was
                  // typed into it: a half-filled cash movement, or the counted
                  // cash inside an open close dialog. See
                  // `.interface-design/system.md`, "preserveState on inline
                  // mutations".
                  onSubmit={(value) =>
                    patchInline(
                      TILL_PATHS.itemStock(stock.id),
                      { quantity: value },
                      { preserveState: true }
                    )
                  }
                />
              </li>
            ))}
          </ul>

          <CardContent className="pt-0">
            <p className="text-xs text-muted-foreground">{tTill("stock_panel_hint")}</p>
          </CardContent>
        </>
      )}

      {locationUser?.role === "manager" ? (
        <CardFooter>
          <Link
            href={TILL_PATHS.manageItemStocks}
            className="text-sm font-medium text-brand-ink"
            data-testid="shift-stock-panel-manage"
          >
            {tTill("stock_manage")}
          </Link>
        </CardFooter>
      ) : null}
    </Card>
  )
}
